/**
 * Passerelle réseau du multi-planning côté adhérent.
 *
 * Le raisonnement lui-même vit dans `@/lib/subscriptionCalendars` (pur,
 * testé) ; ici on ne fait que RÉCUPÉRER les données dont il a besoin :
 *   - les calendriers ouverts par chaque abonnement actif,
 *   - la ventilation par calendrier des réservations à venir.
 *
 * ⚠️ COÛT RÉSEAU — à lire avant d'y toucher. Cinq salles sur six n'utilisent
 * aucun calendrier, mais elles utilisent bien des TYPES d'abonnement :
 * `attachCalendarScopes` ne peut donc pas s'en tirer sans lire les types, et
 * elle ajoute UNE requête (`subscription-types-management/get_types`) au
 * chargement du planning adhérent. C'est le seul appel supplémentaire du
 * chantier multi-planning pour ces salles, et il est mutualisé par le cache
 * ci-dessous (les types changent au rythme des décisions de l'administrateur,
 * pas à celui des rafraîchissements de planning).
 *
 * `fetchUpcomingBookingsByCalendar`, elle, n'est appelée par l'interface que
 * lorsqu'au moins un abonnement actif est réellement restreint : elle reste
 * donc à coût nul pour les salles sans multi-planning.
 */

import { NO_CALENDAR_BUCKET, isUniversalSubscription } from "@/lib/subscriptionCalendars";
import type { CalendarScopedSubscription } from "@/lib/subscriptionCalendars";
import { getSubscriptionTypes } from "@/services/edgeFunctionWrappers/subscriptionTypesWrapper";
import { getUserBookings } from "@/services/bookingsService";
import { classesService } from "@/services/classesService";

export type WithCalendarScope<T> = T & { calendar_ids: string[] | null };

/**
 * Mémo court des liens `type -> calendriers`, par salle.
 *
 * `attachCalendarScopes` est appelée à chaque chargement du planning adhérent,
 * à chaque rafraîchissement silencieux après réservation, et par le hook de
 * libellés sur deux écrans. Sans mémo, la même liste de types est redemandée
 * plusieurs fois par minute pour une donnée qui ne bouge qu'à l'initiative d'un
 * administrateur. 60 s, même ordre de grandeur que le `staleTime` déjà retenu
 * pour les calendriers accessibles (`useAccessibleCalendars`).
 *
 * La promesse elle-même est mémorisée, pas seulement son résultat : deux appels
 * concurrents (planning + libellés au même instant) partagent alors une seule
 * requête. Un échec n'est jamais mis en cache — l'entrée est retirée pour que
 * la tentative suivante reparte du réseau.
 */
const CALENDAR_SCOPE_TTL_MS = 60_000;

const calendarIdsByTypeCache = new Map<
  string,
  { at: number; promise: Promise<Map<string, string[]>> }
>();

/** Vide le mémo (changement de salle, écran d'administration des types…). */
export function invalidateCalendarScopeCache(gymId?: string): void {
  if (gymId) calendarIdsByTypeCache.delete(gymId);
  else calendarIdsByTypeCache.clear();
}

async function loadCalendarIdsByType(gymId: string): Promise<Map<string, string[]>> {
  const cached = calendarIdsByTypeCache.get(gymId);
  if (cached && Date.now() - cached.at < CALENDAR_SCOPE_TTL_MS) {
    return cached.promise;
  }

  // `include_inactive = true` : un abonnement en cours peut porter un type
  // désactivé depuis, ses restrictions de planning restent valables.
  const promise = getSubscriptionTypes(gymId, true)
    .then((types) => new Map(types.map((t) => [t.id, t.calendar_ids ?? []])))
    .catch((error) => {
      calendarIdsByTypeCache.delete(gymId);
      throw error;
    });

  calendarIdsByTypeCache.set(gymId, { at: Date.now(), promise });
  return promise;
}

/**
 * Complète chaque abonnement des calendriers ouverts par son type.
 *
 * - `calendar_ids: []`   → universel (aucun lien) ou abonnement sans type
 * - `calendar_ids: null` → liens NON RÉSOLUS (lecture des types en échec).
 *   `isUniversalSubscription` traite ce cas comme universel : c'est le choix
 *   permissif, celui qui laisse le comportement d'aujourd'hui intact et
 *   confie l'arbitrage au serveur.
 *
 * Ne lève jamais : un échec de lecture des types dégrade vers `null`, il ne
 * doit pas faire échouer le chargement des abonnements eux-mêmes.
 */
export async function attachCalendarScopes<T extends CalendarScopedSubscription>(
  subscriptions: T[],
  gymId: string | null | undefined
): Promise<WithCalendarScope<T>[]> {
  const typed = subscriptions.some((s) => !!s.subscription_type_id);

  // Aucun abonnement typé (ou pas de salle résolue) : rien à aller chercher,
  // tout est universel. C'est le chemin des salles sans multi-planning.
  if (!typed || !gymId) {
    return subscriptions.map((s) => ({ ...s, calendar_ids: [] }));
  }

  let calendarIdsByType: Map<string, string[]> | null = null;
  try {
    calendarIdsByType = await loadCalendarIdsByType(gymId);
  } catch (error) {
    console.error(
      "[subscriptionCalendarsService] Lecture des types d'abonnement en échec, calendriers non résolus:",
      error
    );
  }

  return subscriptions.map((s) => {
    if (!s.subscription_type_id) return { ...s, calendar_ids: [] };
    if (!calendarIdsByType) return { ...s, calendar_ids: null };
    return { ...s, calendar_ids: calendarIdsByType.get(s.subscription_type_id) ?? [] };
  });
}

/** Faut-il ventiler le comptage des réservations par calendrier ? */
export function needsCalendarBreakdown(subscriptions: CalendarScopedSubscription[]): boolean {
  return subscriptions.some((s) => !isUniversalSubscription(s));
}

/**
 * Réservations à venir de l'utilisateur authentifié, ventilées par
 * `calendar_id` (seau `NO_CALENDAR_BUCKET` pour les cours sans calendrier).
 *
 * Reconstruit côté client faute d'un comptage serveur ventilé : les
 * réservations donnent le `classId`, le planning donne le `calendar_id`. La
 * fenêtre de dates interrogée est bornée par les réservations elles-mêmes, pas
 * par une plage arbitraire.
 *
 * Un cours dont le calendrier reste introuvable est rangé dans
 * `NO_CALENDAR_BUCKET`, donc compté pour TOUS les plannings : le doute joue
 * contre une réservation de trop, jamais contre le quota du serveur.
 *
 * Lève en cas d'échec : l'appelant doit alors retomber sur le comptage global,
 * pas sur zéro.
 */
export async function fetchUpcomingBookingsByCalendar(
  gymId: string
): Promise<Record<string, number>> {
  const bookings = await getUserBookings({ status: "upcoming", dateFilter: "upcoming" });

  // Même définition de « à venir » que `get_user_upcoming_count` côté serveur :
  // la journée du cours compte jusqu'à sa fin.
  const now = Date.now();
  const upcoming = bookings.filter((b) => {
    if (!b.date) return false;
    const d = new Date(b.date);
    if (Number.isNaN(d.getTime())) return false;
    d.setHours(23, 59, 59, 999);
    return d.getTime() > now;
  });

  if (upcoming.length === 0) return {};

  const dates = upcoming.map((b) => b.date).sort();
  const classes = await classesService.getScheduledClasses(
    gymId,
    dates[0],
    dates[dates.length - 1]
  );

  const calendarByClassId = new Map<string, string | null>();
  for (const c of classes) {
    if (!calendarByClassId.has(c.id)) calendarByClassId.set(c.id, c.calendar_id ?? null);
  }

  const byCalendar: Record<string, number> = {};
  for (const booking of upcoming) {
    const calendarId = calendarByClassId.get(booking.classId) ?? null;
    const key = calendarId ?? NO_CALENDAR_BUCKET;
    byCalendar[key] = (byCalendar[key] ?? 0) + 1;
  }
  return byCalendar;
}
